import { Component } from 'preact';
import { cn } from '../lib/utils';
import CardInspector, { RARITY_LABEL_COLOR } from './CardInspector';
import DeckCardTile from './DeckCardTile';
import FoilCanvas from './FoilCanvas';
import CardGrid from './CardGrid';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../utils/sorcery/foilParams.js';
import ElementIcon from './deckStats/ElementIcon';
import DeckEditorSidebar from './DeckEditorSidebar';
import { ART_GROUP_BASE, artGroupOf } from '../utils/sorcery/artGroups';
import AmbientParticles from './AmbientParticles';
import RuneSpinner from './RuneSpinner';
import { playUI, UI } from '../utils/arena/uiSounds';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { submitDraftDeck, subscribeToDraftEvents, saveDraftWorkingDeck, getDraftWorkingDeck } from '../utils/arena/draftApi';
import { resolveAvatarRules } from '../utils/game/avatarRules';
import { BackButton } from './ui/navButtons';
import { resolvePackContents } from '../utils/arena/packsApi';
import { Select } from './ui/select';
import CardFilterBar from './CardFilterBar';
import CardSortChips from './CardSortChips';
import { getPref, setPref } from '../utils/persistentPrefs';
import {
  CARD_SORT_PREF_KEY, CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC,
  normalizeCardSort, normalizeCardSortDir, makeCardComparator, nextCardSort,
} from '../utils/sorcery/cardSort';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, PANEL_BG,
  BEVELED_BTN, GOLD_BTN, RADIUS,
} from '../lib/medievalTheme';
import { t } from '../localization/t';

const MIN_SPELLBOOK = 24;
const MIN_ATLAS = 12;

const BASIC_SITE_NAMES = ['Spire', 'Stream', 'Valley', 'Wasteland'];
const BASIC_SITE_ELEMENTS = { Spire: 'Air', Stream: 'Water', Valley: 'Earth', Wasteland: 'Fire' };

// Resolve basic sites and Spellslinger from the real card database where possible.
// Cards not found get a minimal placeholder that the sidebar can render (with element initial).
function resolveBasicSites(sorceryCards) {
  return BASIC_SITE_NAMES.map((name) => {
    const real = (sorceryCards || []).find((c) => c.name === name && (c.type === 'Site' || c._sorceryCategory === 'Site'));
    if (real) return real;
    return {
      unique_id: `basic-${name.toLowerCase()}`,
      name,
      type: 'Site',
      _sorceryCategory: 'Site',
      played_horizontally: true,
      rarity: 'Ordinary',
      printings: [{ foiling: 'S', unique_id: `basic-${name.toLowerCase()}-s` }],
    };
  });
}

function resolveSpellslinger(sorceryCards) {
  const real = (sorceryCards || []).find((c) => c.name === 'Spellslinger' && (c.type === 'Avatar' || c._sorceryCategory === 'Avatar'));
  if (real) return real;
  return {
    unique_id: 'spellslinger',
    name: 'Spellslinger',
    type: 'Avatar',
    _sorceryCategory: 'Avatar',
    rarity: 'Unique',
    printings: [{ foiling: 'S' }],
  };
}

function isAvatarCard(card) { return card?.type === 'Avatar' || card?._sorceryCategory === 'Avatar'; }
function isSiteCard(card) { return card?.type === 'Site' || card?._sorceryCategory === 'Site' || card?.played_horizontally; }

export default class DraftDeckBuilder extends Component {
  constructor(props) {
    super(props);

    const rawDrafted = props.draftedCards || [];
    const resolved = resolvePackContents(rawDrafted, props.sorceryCards || []);

    const spellslinger = resolveSpellslinger(props.sorceryCards);
    const basicSites = resolveBasicSites(props.sorceryCards);

    // Keep one entry per drafted avatar card so we preserve foiling
    // through submission. If a player somehow drafted the same avatar in
    // both standard and foil, prefer the foil printing.
    const draftedAvatarMap = new Map();
    for (const entry of resolved) {
      if (!isAvatarCard(entry.card)) continue;
      const id = entry.card.unique_id;
      const existing = draftedAvatarMap.get(id);
      const newIsFoil = entry.printing?.foiling === 'F';
      const existingIsFoil = existing?.printing?.foiling === 'F';
      if (!existing || (newIsFoil && !existingIsFoil)) {
        draftedAvatarMap.set(id, { card: entry.card, printing: entry.printing });
      }
    }
    const spellslingerEntry = { card: spellslinger, printing: spellslinger.printings?.[0] || {} };
    const availableAvatars = [spellslingerEntry, ...draftedAvatarMap.values()];

    const initialChosen = [{
      card: spellslinger,
      printing: spellslingerEntry.printing,
      zone: 'avatar',
      uniqueId: spellslinger.unique_id,
      foiling: spellslingerEntry.printing?.foiling || 'S',
    }];

    // Distinct non-avatar cards in the pool. Feeds the filter bar so its
    // keyword / subtype / artist options only list what was actually drafted.
    const seen = new Set();
    this.poolCards = resolved
      .map((e) => e.card)
      .filter((c) => c?.unique_id && !isAvatarCard(c) && !seen.has(c.unique_id) && seen.add(c.unique_id));

    this.state = {
      resolvedPool: resolved,
      // Filter predicates come from the CardFilterBar child (emits on mount).
      matchesCard: () => true,
      matchesFoiling: () => true,
      sortBy: normalizeCardSort(getPref(CARD_SORT_PREF_KEY, 'name')),
      sortDir: normalizeCardSortDir(getPref(CARD_SORT_DIR_PREF_KEY, CARD_SORT_DIR_ASC)),
      chosenCards: initialChosen,
      avatar: spellslinger,
      availableAvatars,
      basicSites,
      timeLeft: 30 * 60,
      submitting: false,
      submitted: false,
      error: null,
      hoveredIndex: -1,
      inspectedEntry: null,
      // WebGL foil overlay for the sidebar hover preview (single focused
      // card). null when the hovered card isn't a foil; otherwise the
      // image url + zones for the FoilCanvas. Sites are skipped because the
      // preview rotates only the <img>, not the wrapper, so a portrait
      // FoilCanvas quad wouldn't line up with the landscape box.
      previewFoil: null,
    };
    this.timer = null;
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    this.unsubDraft = subscribeToDraftEvents({
      timer: (data) => { if (data.phase === 'building') this.setState({ timeLeft: data.timeLeft }); },
      round_start: (data) => { this.props.onTournamentStart?.(data); },
    });
    this.timer = setInterval(() => {
      this.setState((s) => ({ timeLeft: Math.max(0, s.timeLeft - 1) }));
    }, 1000);
    // Restore in-progress build state (zone assignments + avatar) from the
    // server, then autosave it periodically so a disconnect or a Leave
    // doesn't reset the player's deck-building progress.
    this._loadWorkingDeck();
    this._saveIv = setInterval(this._flushWorkingDeck, 8000);
  }

  // --- In-progress deck persistence (survives leave/rejoin) ---

  _loadWorkingDeck = async () => {
    try {
      const wd = await getDraftWorkingDeck(this.props.eventId);
      if (this._unmounted || this.state.submitted) { this._loaded = true; return; }
      const restored = this._reconstructWorkingDeck(wd);
      if (restored && restored.chosenCards.length) {
        this.setState({
          chosenCards: restored.chosenCards,
          avatar: restored.avatar || this.state.avatar,
        });
      }
    } catch (err) {
      console.warn('[DraftDeckBuilder] load working deck failed:', err?.message || err);
    } finally {
      // Gate autosave on a completed load so the first tick can't overwrite
      // the saved deck with the default starting state.
      this._loaded = true;
    }
  };

  _serializeWorkingDeck() {
    return {
      avatarId: this.state.avatar?.unique_id || null,
      cards: (this.state.chosenCards || [])
        .filter((e) => e.card?.unique_id)
        .map((e) => ({
          cardId: e.card.unique_id,
          printingId: e.printing?.unique_id || null,
          foiling: e.printing?.foiling || 'S',
          zone: e.zone || 'spellbook',
        })),
    };
  }

  // Rebuild chosenCards from the saved {cardId, printingId, foiling, zone}
  // list by consuming matching entries from the drafted pool (and avatars).
  // Defensive: unmatched entries are skipped, and an empty result returns
  // null so the default starting deck stays.
  _reconstructWorkingDeck(wd) {
    if (!wd || !Array.isArray(wd.cards) || !wd.cards.length) return null;
    // Carry the original pool index. GetUnassignedPool excludes pool entries
    // whose index appears in a chosen card's _poolIndex. Without it the chosen
    // cards would ALSO show in the pickable pool, letting a player duplicate
    // them by leaving and re-adding (a real exploit).
    const pool = (this.state.resolvedPool || []).map((e, i) => ({ e, i, used: false }));
    const chosen = [];
    let avatarCard = null;
    for (const saved of wd.cards) {
      if (saved.zone === 'avatar') {
        const av = (this.state.availableAvatars || []).find((a) => a.card?.unique_id === saved.cardId);
        if (av) {
          chosen.push({ card: av.card, printing: av.printing, zone: 'avatar', uniqueId: av.card.unique_id, foiling: av.printing?.foiling || 'S' });
          avatarCard = av.card;
        }
        continue;
      }
      const match = pool.find((p) => !p.used
        && p.e.card?.unique_id === saved.cardId
        && (saved.printingId == null || p.e.printing?.unique_id === saved.printingId));
      if (!match) continue;
      match.used = true;
      chosen.push({
        card: match.e.card,
        printing: match.e.printing,
        zone: saved.zone || 'spellbook',
        uniqueId: match.e.card.unique_id,
        foiling: match.e.printing?.foiling || 'S',
        _poolIndex: match.i,
      });
    }
    if (!chosen.length) return null;
    if (!avatarCard && wd.avatarId) {
      avatarCard = (this.state.availableAvatars || []).find((a) => a.card?.unique_id === wd.avatarId)?.card || null;
    }
    return { chosenCards: chosen, avatar: avatarCard };
  }

  _flushWorkingDeck = () => {
    if (this._unmounted || !this._loaded || this.state.submitted) return;
    saveDraftWorkingDeck(this.props.eventId, this._serializeWorkingDeck()).catch(() => {});
  };

  componentDidUpdate(_prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.hoveredIndex >= 0 &&
      this.state.hoveredIndex !== prevState.hoveredIndex &&
      this.inspectorHold.consumePending()
    ) {
      const pool = this.getVisiblePool();
      const entry = pool[this.state.hoveredIndex];
      if (entry) {
        playUI(UI.INSPECTOR_OPEN);
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ inspectedEntry: entry });
      }
    }

    const inspectorOpen = this.state.inspectedEntry != null;
    const wasInspectorOpen = prevState.inspectedEntry != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => {
        playUI(UI.INSPECTOR_CLOSE);
        this.setState({ inspectedEntry: null });
      });
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  componentWillUnmount() {
    // Flush a final save so leaving captures the latest build (the periodic
    // tick may be up to 8s stale). Must run before _unmounted is set.
    this._flushWorkingDeck();
    this._unmounted = true;
    clearInterval(this._saveIv);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
    this.unsubDraft?.();
    clearInterval(this.timer);
    if (this._previewRafId) cancelAnimationFrame(this._previewRafId);
  }

  handleKeyDown = (e) => {
    if (e.repeat) return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      if (this.state.inspectedEntry) {
        playUI(UI.INSPECTOR_CLOSE);
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredIndex >= 0) {
        const pool = this.getVisiblePool();
        if (pool[this.state.hoveredIndex]) {
          playUI(UI.INSPECTOR_OPEN);
          this.inspectorHold.noteOpen();
          this.setState({ inspectedEntry: pool[this.state.hoveredIndex] });
        }
      } else {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    playUI(UI.INSPECTOR_CLOSE);
    this.setState({ inspectedEntry: null });
  };

  getUnassignedPool() {
    const { resolvedPool, chosenCards } = this.state;
    const usedIndexes = new Set(chosenCards.filter((e) => e._poolIndex != null).map((e) => e._poolIndex));
    return resolvedPool
      .map((entry, i) => ({ ...entry, _poolIndex: i }))
      .filter((entry) => !usedIndexes.has(entry._poolIndex) && !isAvatarCard(entry.card));
  }

  // Unassigned pool after the filter bar + sort. Hover index, inspector and
  // the grid all read this list so they agree on which tile is which.
  getVisiblePool() {
    const { matchesCard, matchesFoiling, sortBy, sortDir } = this.state;
    return this.getUnassignedPool()
      .filter((e) => matchesCard(e.card) && matchesFoiling(e.printing?.foiling))
      .sort(makeCardComparator(sortBy, (e) => e.card, sortDir));
  }

  handleFilterChange = ({ matchesCard, matchesFoiling }) => {
    this.setState({ matchesCard, matchesFoiling, hoveredIndex: -1 });
  };

  handleSortByChange = (value) => {
    playUI(UI.HOVER, { volume: 0.5 });
    const next = nextCardSort(this.state, value);
    if (!next) return;
    setPref(CARD_SORT_PREF_KEY, next.sortBy);
    setPref(CARD_SORT_DIR_PREF_KEY, next.sortDir);
    this.setState({ ...next, hoveredIndex: -1 });
  };

  handleAddCard = (entry) => {
    if (this.state.submitted) return;
    const card = entry.card;
    if (!card || isAvatarCard(card)) return;

    const zone = isSiteCard(card) ? 'atlas' : 'spellbook';
    this.setState((s) => ({
      chosenCards: [...s.chosenCards, {
        card, printing: entry.printing || {}, zone,
        uniqueId: card.unique_id, foiling: entry.printing?.foiling || 'S',
        _poolIndex: entry._poolIndex,
      }],
    }));
    playUI(UI.CARD_PLACE);
  };

  handleRemoveCard = (uniqueId, artGroup, foiling) => {
    if (this.state.submitted) return;
    this.setState((s) => {
      const idx = s.chosenCards.findIndex((e) =>
        e.card?.unique_id === uniqueId
        && artGroupOf(e.printing) === artGroup
        && (e.printing?.foiling || 'S') === foiling
        && e.zone !== 'avatar'
      );
      if (idx === -1) return null;
      const next = [...s.chosenCards];
      next.splice(idx, 1);
      return { chosenCards: next };
    });
  };

  handleIncrement = (uniqueId, artGroup, foiling) => {
    if (this.state.submitted) return;
    const pool = this.getUnassignedPool();
    const entry = pool.find((e) =>
      e.card?.unique_id === uniqueId
      && artGroupOf(e.printing) === artGroup
      && (e.printing?.foiling || 'S') === foiling
    );
    if (entry) this.handleAddCard(entry);
  };

  handleChangeZone = (uniqueId, newZone, artGroup, foiling) => {
    if (this.state.submitted) return;
    this.setState((s) => {
      // Move a single copy. The previous .map() rewrote every matching
      // entry at once, so a hotkey-press or context-menu move on one
      // copy of a card silently dragged its siblings along. Skipping
      // entries already in the target zone also avoids the "C only
      // works once" symptom DeckEditor used to have.
      const idx = s.chosenCards.findIndex((c) =>
        c.card?.unique_id === uniqueId
        && artGroupOf(c.printing) === artGroup
        && (c.printing?.foiling || 'S') === foiling
        && c.zone !== newZone,
      );
      if (idx === -1) return null;
      const next = [...s.chosenCards];
      next[idx] = { ...next[idx], zone: newZone };
      return { chosenCards: next };
    });
  };

  addBasicSite = (siteCard) => {
    if (this.state.submitted) return;
    this.setState((s) => ({
      chosenCards: [...s.chosenCards, {
        card: siteCard,
        printing: siteCard.printings?.[0] || {},
        zone: 'atlas',
        uniqueId: siteCard.unique_id,
        foiling: 'S',
      }],
    }));
    playUI(UI.CARD_PLACE);
  };

  removeBasicSite = (e, siteCard) => {
    e.preventDefault();
    if (this.state.submitted) return;
    this.setState((s) => {
      const idx = s.chosenCards.findIndex((c) => c.card?.unique_id === siteCard.unique_id);
      if (idx === -1) return null;
      const next = [...s.chosenCards];
      next.splice(idx, 1);
      return { chosenCards: next };
    });
  };

  setAvatar = (avatarId) => {
    if (this.state.submitted) return;
    const entry = this.state.availableAvatars.find((e) => (e.card.unique_id || e.card.cardId) === avatarId);
    if (!entry) return;

    this.setState((s) => ({
      avatar: entry.card,
      chosenCards: [
        // Replace avatar entry. Preserve the pack's printing so a foil
        // avatar stays foil on the table.
        {
          card: entry.card,
          printing: entry.printing || entry.card.printings?.[0] || {},
          zone: 'avatar',
          uniqueId: entry.card.unique_id || entry.card.cardId,
          foiling: entry.printing?.foiling || 'S',
        },
        ...s.chosenCards.filter((e) => e.zone !== 'avatar'),
      ],
    }));
  };

  getBasicSiteCount(uniqueId) {
    return this.state.chosenCards.filter((e) => e.card?.unique_id === uniqueId).length;
  }

  getTotalAtlasCount() {
    return this.state.chosenCards.filter((e) => e.zone === 'atlas').length;
  }

  getSpellbookCount() {
    return this.state.chosenCards.filter((e) => e.zone === 'spellbook').length;
  }

  // Magician ("you have no atlas, but your spellbook may contain sites")
  // builds ONE sleeved deck. The separate 12-site atlas floor cannot apply,
  // so the gate becomes a combined deck minimum of the same total size.
  avatarForbidsAtlas() {
    return !!resolveAvatarRules(this.state.avatar).deckbuilding?.forbidAtlas;
  }

  isValid() {
    if (!this.state.avatar) return false;
    if (this.avatarForbidsAtlas()) {
      return this.getSpellbookCount() + this.getTotalAtlasCount() >= MIN_SPELLBOOK + MIN_ATLAS;
    }
    return this.getSpellbookCount() >= MIN_SPELLBOOK && this.getTotalAtlasCount() >= MIN_ATLAS;
  }

  handleSubmit = async () => {
    if (!this.isValid() || this.state.submitting) return;
    this.setState({ submitting: true, error: null });

    // Flatten to the same shape constructed-deck saves use: a single `cards`
    // array with each entry carrying its own `printingId` so foiling is
    // preserved through to the match. The avatar is just another entry.
    // the game board identifies it by card.type when spawning.
    //
    // Collection-zoned chosen cards and unassigned pool leftovers are sent
    // as `isSideboard: true` so the game engine routes them into the
    // in-match Collection pile (gameState.js) instead of shuffling them
    // into the playable spellbook/atlas. Permanent-collection grant runs
    // server-side off draft_picks and is independent of this payload.
    const { chosenCards, resolvedPool } = this.state;
    const usedPoolIndexes = new Set(
      chosenCards.filter((e) => e._poolIndex != null).map((e) => e._poolIndex),
    );

    const chosenEntries = chosenCards
      .filter((e) => e.card?.unique_id)
      .map((e) => ({
        cardId: e.card.unique_id,
        cardName: e.card.name,
        printingId: e.printing?.unique_id,
        isSideboard: e.zone === 'collection',
      }));

    const leftoverEntries = resolvedPool
      .filter((entry, i) => !usedPoolIndexes.has(i) && entry.card?.unique_id && !isAvatarCard(entry.card))
      .map((entry) => ({
        cardId: entry.card.unique_id,
        cardName: entry.card.name,
        printingId: entry.printing?.unique_id,
        isSideboard: true,
      }));

    const cards = [...chosenEntries, ...leftoverEntries];

    try {
      await submitDraftDeck(this.props.eventId, { cards });
      this.setState({ submitting: false });
      // Go straight to the tournament dashboard (the waiting room). Never
      // block on a "waiting for other players" overlay. That hangs forever
      // if another player leaves before the round can start. From the
      // dashboard the player can see status, wait, or leave.
      if (this.props.onDeckSubmitted) {
        this.props.onDeckSubmitted();
      } else {
        this.setState({ submitted: true });
      }
    } catch (err) {
      this.setState({ submitting: false, error: err.message });
    }
  };

  // --- Sidebar hover preview (same RAF system as DeckEditor) ---

  _previewAnim = { opacity: 0, top: 0, targetTop: 0, visible: false, imgUrl: '', cardName: '', isSite: false, isFoil: false, foiling: '', sidebarLeft: 0 };
  _previewRafId = null;

  handleSidebarHover = (cardUniqueId, hoverArtGroup, hoverFoiling, rowElement) => {
    if (!cardUniqueId) {
      this._previewAnim.visible = false;
      if (this.state.previewFoil) this.setState({ previewFoil: null });
      this._startPreviewLoop();
      return;
    }
    const targetGroup = hoverArtGroup || ART_GROUP_BASE;
    const targetFoiling = hoverFoiling || 'S';
    const card = this.state.chosenCards.find((c) =>
      c.card?.unique_id === cardUniqueId
      && artGroupOf(c.printing) === targetGroup
      && (c.printing?.foiling || 'S') === targetFoiling
    ) || this.state.chosenCards.find((c) => c.card?.unique_id === cardUniqueId);
    if (!card) return;
    // Prefer the exact hovered element. The same card can appear in multiple
    // zones, and querySelector would otherwise pick the first DOM match.
    const row = rowElement
      || document.querySelector(`[data-sidebar-card-id="${cardUniqueId}"][data-sidebar-art-group="${targetGroup}"][data-sidebar-foiling="${targetFoiling}"]`)
      || document.querySelector(`[data-sidebar-card-id="${cardUniqueId}"]`);
    const rect = row?.getBoundingClientRect();
    if (!rect) return;

    // Preview is `position: fixed` inside .app-canvas (canvas is the
    // containing block), so top/left are canvas-local. Convert from
    // viewport rect and clamp against canvas height.
    const canvasEl = document.querySelector('.app-canvas');
    const canvasRect = canvasEl ? canvasEl.getBoundingClientRect() : { left: 0, top: 0, height: window.innerHeight };
    const canvasH = canvasRect.height;
    const localCenterY = (rect.top - canvasRect.top) + rect.height / 2;
    const localLeft = rect.left - canvasRect.left;

    const imgUrl = card.printing?.image_url || card.card?.printings?.[0]?.image_url || '';
    const isSite = card.card?.played_horizontally;
    const foiling = card.printing?.foiling || 'S';
    const isFoil = foiling === 'F' || foiling === 'R';
    const previewW = isSite ? 420 : 300;
    const previewH = isSite ? 300 : 420;
    let targetTop = localCenterY - previewH / 2;
    if (targetTop < 8) targetTop = 8;
    if (targetTop + previewH > canvasH - 8) targetTop = canvasH - 8 - previewH;

    const anim = this._previewAnim;
    const wasVisible = anim.visible;
    anim.visible = true;
    anim.imgUrl = imgUrl;
    anim.cardName = card.card?.name || '';
    anim.isSite = isSite;
    anim.isFoil = isFoil;
    anim.foiling = foiling;
    anim.sidebarLeft = localLeft;
    anim.targetTop = targetTop;
    anim.previewW = previewW;
    anim.previewH = previewH;
    if (!wasVisible) { anim.top = targetTop; anim.opacity = 0; }

    // Mount the premium WebGL foil overlay for non-site foil previews (the
    // legacy CSS foil was removed). Skip sites. Only the <img> rotates, so
    // a portrait FoilCanvas quad wouldn't match the landscape preview box.
    const wantFoil = isFoilFinish(foiling) && !isSite;
    const prev = this.state.previewFoil;
    if (wantFoil) {
      if (!prev || prev.imgUrl !== imgUrl) {
        this.setState({ previewFoil: { imgUrl, zones: foilZonesForCard(card.card) } });
      }
    } else if (prev) {
      this.setState({ previewFoil: null });
    }

    this._startPreviewLoop();
  };

  _startPreviewLoop = () => {
    if (this._previewRafId) return;
    this._previewRafId = requestAnimationFrame(this._tickPreview);
  };

  _tickPreview = () => {
    this._previewRafId = null;
    const anim = this._previewAnim;
    const speed = 0.18;
    if (anim.visible) {
      anim.opacity += (1 - anim.opacity) * speed;
      if (anim.opacity > 0.99) anim.opacity = 1;
    } else {
      anim.opacity += (0 - anim.opacity) * (speed * 1.5);
      if (anim.opacity < 0.01) anim.opacity = 0;
    }
    anim.top += (anim.targetTop - anim.top) * speed;

    const el = this._previewRef;
    if (el) {
      const { previewW = 300, previewH = 420 } = anim;
      const gap = 16;
      const left = anim.sidebarLeft - previewW - gap;
      el.style.opacity = String(anim.opacity);
      el.style.top = `${anim.top}px`;
      el.style.left = `${left}px`;
      el.style.width = `${previewW}px`;
      el.style.height = `${previewH}px`;
      el.style.transform = `scale(${0.85 + anim.opacity * 0.15})`;
      el.style.display = anim.opacity > 0.01 ? 'block' : 'none';

      const inner = el.querySelector('[data-preview-inner]');
      if (inner) inner.className = 'relative w-full h-full rounded-card card-mask';

      const img = el.querySelector('img');
      if (img && img.src !== anim.imgUrl && anim.imgUrl) img.src = anim.imgUrl;
      if (img) {
        img.alt = anim.cardName;
        if (anim.isSite) {
          img.style.transform = 'rotate(90deg)';
          img.style.transformOrigin = 'center center';
          img.style.width = `${previewH}px`;
          img.style.height = `${previewW}px`;
          img.style.position = 'absolute';
          img.style.top = `${(previewH - previewW) / 2}px`;
          img.style.left = `${(previewW - previewH) / 2}px`;
        } else {
          img.style.transform = 'none';
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.position = 'static';
          img.style.top = '';
          img.style.left = '';
        }
      }
    }

    const isSettled = anim.visible ? anim.opacity >= 1 && Math.abs(anim.top - anim.targetTop) < 0.5 : anim.opacity <= 0;
    if (!isSettled) this._previewRafId = requestAnimationFrame(this._tickPreview);
  };

  // --- CardGrid wiring for the draft pool ---

  // Stable key per pool entry. _poolIndex is the original pool slot, unique
  // even when the same card was drafted multiple times.
  getPoolItemKey = (entry) => `pool-${entry.card?.unique_id}-${entry._poolIndex}`;

  // Foil descriptor: foil pool entries draw their art in the shared WebGL
  // canvas (imageHidden on the DOM tile); non-foil return null and keep the
  // DOM <img>.
  getPoolItemFoil = (entry) => {
    const foiling = entry.printing?.foiling || 'S';
    if (!isFoilFinish(foiling)) return null;
    return { imageUrl: entry.printing?.image_url || '', zones: foilZonesForCard(entry.card) };
  };

  renderPoolTile = (entry, { imageHidden, onTilt, index }) => (
    <DeckCardTile
      entry={{ card: entry.card, printing: entry.printing || {}, zone: 'spellbook', entryIndex: index }}
      isSelected={false}
      imageHidden={imageHidden}
      onTilt={onTilt}
      onClick={() => this.handleAddCard(entry)}
      onHoverChange={(hovered) => {
        this.setState({ hoveredIndex: hovered ? index : -1 });
        if (hovered) playUI(UI.HOVER, { volume: 0.4 });
      }}
    />
  );

  render() {
    const { chosenCards, avatar, availableAvatars, basicSites, timeLeft, submitting, submitted, error, hoveredIndex, inspectedEntry } = this.state;

    const pool = this.getVisiblePool();
    const unassignedCount = this.getUnassignedPool().length;
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    const timerUrgent = timeLeft <= 120;
    const totalAtlas = this.getTotalAtlasCount();
    const spellbookCount = this.getSpellbookCount();
    const valid = this.isValid();

    const avatarOptions = availableAvatars.map((e) => {
      const isFoil = e.printing?.foiling === 'F';
      return {
        value: e.card.unique_id || e.card.cardId || 'spellslinger',
        label: isFoil ? t('{name} (Foil)', { name: e.card.name || t('Unknown') }) : (e.card.name || t('Unknown')),
      };
    });

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden select-none" style={{ background: '#08080a' }}>
        <div className="absolute inset-0" style={{ background: `url('/hub-bg.webp') center/cover no-repeat`, filter: 'blur(6px)', transform: 'scale(1.02)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8) 100%)' }} />
        <AmbientParticles />

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-2.5 relative z-20" style={{ background: PANEL_BG, borderBottom: `1px solid ${GOLD} 0.15)`, backdropFilter: 'blur(8px)' }}>
          {this.props.onBack ? <BackButton title={t('Leave draft')} onClick={this.props.onBack} /> : null}
          <div className="text-sm font-bold arena-heading" style={{ color: ACCENT_GOLD }}>{t('Build Your Deck')}</div>
          <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
            {this.avatarForbidsAtlas() ? (
              <>{t('Deck')} <span style={{ color: spellbookCount + totalAtlas >= MIN_SPELLBOOK + MIN_ATLAS ? '#6dba6d' : '#e06060' }}>{spellbookCount + totalAtlas}/{MIN_SPELLBOOK + MIN_ATLAS}+</span></>
            ) : (
              <>
                {t('Spellbook')} <span style={{ color: spellbookCount >= MIN_SPELLBOOK ? '#6dba6d' : '#e06060' }}>{spellbookCount}/{MIN_SPELLBOOK}+</span>
                {' · '}{t('Atlas')} <span style={{ color: totalAtlas >= MIN_ATLAS ? '#6dba6d' : '#e06060' }}>{totalAtlas}/{MIN_ATLAS}+</span>
              </>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            {error ? <div className="text-[0.625cqw]" style={{ color: '#e06060' }}>{error}</div> : null}
            <div
              className="text-lg font-bold tabular-nums px-3 py-0.5 rounded"
              style={{ color: timerUrgent ? '#e06060' : TEXT_PRIMARY, background: timerUrgent ? 'rgba(224,96,96,0.1)' : 'rgba(0,0,0,0.3)' }}
            >
              {minutes}:{String(seconds).padStart(2, '0')}
            </div>
            <button
              type="button"
              className="px-5 py-1.5 text-xs font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] disabled:pointer-events-none"
              style={{ ...(valid && !submitted ? GOLD_BTN : BEVELED_BTN), opacity: (!submitting && (!valid || submitted)) ? 0.4 : 1 }}
              data-sound={UI.CONFIRM}
              disabled={!valid || submitting || submitted}
              onClick={this.handleSubmit}
            >
              {submitted ? t('Submitted!') : submitting ? <span className="relative"><span className="invisible">{t('Submit Deck')}</span><span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={18} /></span></span> : t('Submit Deck')}
            </button>
          </div>
        </div>

        {/* Avatar + Basic Sites bar. Below header, like the filter bar in DeckEditor */}
        <div className="relative z-20 flex items-center gap-6 px-6 py-2" style={{ background: 'rgba(12,10,8,0.85)', borderBottom: `1px solid ${GOLD} 0.1)` }}>
          {/* Avatar selector */}
          <div className="flex items-center gap-2">
            <span className="text-[0.625cqw] font-semibold" style={{ color: `${GOLD} 0.55)` }}>{t('Avatar')}</span>
            <div style={{ width: '10cqw' }}>
              <Select
                ariaLabel={t('Avatar')}
                options={avatarOptions}
                value={avatar?.unique_id || avatar?.cardId || 'spellslinger'}
                onValueChange={this.setAvatar}
              />
            </div>
          </div>

          {/* Basic sites. Miniature card tiles */}
          <div className="flex items-center gap-2">
            <span className="text-[0.625cqw] font-semibold" style={{ color: `${GOLD} 0.55)` }}>{t('Basic Sites')}</span>
            <div className="flex gap-2">
              {basicSites.map((siteCard) => {
                const count = this.getBasicSiteCount(siteCard.unique_id);
                const element = BASIC_SITE_ELEMENTS[siteCard.name] || '';
                const imgUrl = siteCard.printings?.[0]?.image_url || '';
                // Wrapper is landscape (88:63 wide). The underlying
                // card image is stored portrait (63:88), so it has to
                // be rendered at the *swapped* dimensions and rotated
                // 90° clockwise to fit. Rotating a same-size <img> in
                // place wouldn't work. `object-cover` runs before the
                // transform, so we'd see a cropped strip of the original
                // portrait artwork instead of a clean landscape view.
                const wrapperW = '3.5cqw';
                const wrapperH = `${(3.5 * 63 / 88).toFixed(3)}cqw`;
                return (
                  <div
                    key={siteCard.unique_id}
                    className="flex items-center gap-1.5 cursor-pointer group"
                    onClick={() => this.addBasicSite(siteCard)}
                    onContextMenu={(e) => this.removeBasicSite(e, siteCard)}
                    title={t('{name} ({element}). Left-click to add, right-click to remove', { name: siteCard.name, element })}
                  >
                    <ElementIcon element={element} size={18} />
                    <div className="flex flex-col items-center">
                      <div
                        className="relative transition-all group-hover:scale-[1.06] group-active:scale-[0.95]"
                        style={{
                          width: wrapperW,
                          height: wrapperH,
                          // Match the painted card rounding so the
                          // selection border traces the actual visible
                          // edge of the artwork, not the alpha box.
                          borderRadius: RADIUS.cardLandscape,
                          border: count > 0 ? `2px solid ${ACCENT_GOLD}` : '2px solid transparent',
                          boxShadow: count > 0 ? `0 0 8px rgba(212,168,67,0.25)` : 'none',
                          overflow: 'hidden',
                        }}
                      >
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={siteCard.name}
                            draggable={false}
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              // Swap dimensions: image is drawn at the
                              // wrapper's *post-rotation* portrait size,
                              // then rotated 90° to fill the landscape
                              // wrapper exactly.
                              width: wrapperH,
                              height: wrapperW,
                              transform: 'translate(-50%, -50%) rotate(90deg)',
                              objectFit: 'cover',
                            }}
                          />
                        ) : null}
                      </div>
                      <span className="text-[0.625cqw] tabular-nums font-semibold mt-0.5" style={{ color: count > 0 ? ACCENT_GOLD : TEXT_MUTED }}>
                        {count}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="relative z-10 flex-1 flex min-h-0">
          {/* Left: Card pool */}
          <div className="flex-1 min-w-0 flex flex-col px-4 py-2">
            {/* Same filter bar as the deck editor, minus scope (In Deck /
                Owned / All means nothing for a draft pool). */}
            <CardFilterBar
              className="mb-2"
              sorceryCards={this.poolCards}
              features={{ scope: false, finish: true, type: true }}
              onChange={this.handleFilterChange}
              bottomTrailing={(
                <CardSortChips sortBy={this.state.sortBy} sortDir={this.state.sortDir} onSelect={this.handleSortByChange} />
              )}
            />
            <div className="text-[0.625cqw] font-semibold mb-1" style={{ color: TEXT_MUTED }}>
              {pool.length === unassignedCount
                ? t('Draft Pool ({n} remaining). Click to add · Space to inspect', { n: unassignedCount })
                : t('Draft Pool ({shown} of {total} remaining). Click to add · Space to inspect', { shown: pool.length, total: unassignedCount })}
            </div>
            <div
              className="flex-1 flex min-h-0 relative"
              onMouseLeave={() => this.setState({ hoveredIndex: -1 })}
            >
              {/* Pool cards rendered through the shared CardGrid so every foil
                  draws through the one WebGL context (no per-tile FoilCanvas
                  churn). CardGrid owns its own scroll container. */}
              <CardGrid
                items={pool}
                gridSize={6}
                getItemKey={this.getPoolItemKey}
                getItemFoil={this.getPoolItemFoil}
                renderTile={this.renderPoolTile}
              />
              {pool.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-xs" style={{ color: TEXT_MUTED }}>
                  {unassignedCount === 0 ? t('All cards assigned to deck') : t('No cards match the filters')}
                </div>
              ) : null}
            </div>
          </div>

          {/* Right: DeckEditorSidebar */}
          <div className="w-[18.75cqw] shrink-0 overflow-hidden h-full">
            <DeckEditorSidebar
              chosenCards={chosenCards}
              draftMode
              onIncrement={this.handleIncrement}
              onDecrement={this.handleRemoveCard}
              onChangeZone={this.handleChangeZone}
              onCardHover={this.handleSidebarHover}
            />
          </div>
        </div>

        {/* Sidebar hover preview */}
        <div
          ref={(el) => { this._previewRef = el; }}
          className="fixed pointer-events-none z-[60]"
          style={{ display: 'none', opacity: 0, borderRadius: RADIUS.card, boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 30px rgba(0,0,0,0.4)', transformOrigin: 'right center', willChange: 'transform, opacity, top' }}
        >
          <div data-preview-inner className="relative w-full h-full rounded-card card-mask">
            <img src="" alt="" className="object-cover" draggable={false} />
            {this.state.previewFoil ? (
              <FoilCanvas
                imageUrl={this.state.previewFoil.imgUrl}
                tilt={{ mx: 50, my: 50, fromCenter: 0 }}
                zones={this.state.previewFoil.zones}
                params={DEFAULT_FOIL_PARAMS}
                priority
              />
            ) : null}
          </div>
        </div>

        {/* Card inspector */}
        {inspectedEntry ? (
          <CardInspector
            card={inspectedEntry.card}
            imageUrl={inspectedEntry.printing?.image_url}
            rarity={inspectedEntry.rarity || inspectedEntry.card?.rarity}
            foiling={inspectedEntry.printing?.foiling}
            zoom={1}
            onClose={() => { playUI(UI.INSPECTOR_CLOSE); this.setState({ inspectedEntry: null }); }}
          />
        ) : null}

        {/* Submitted overlay. Blocks further interaction */}
        {submitted ? (
          <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
            <div className="flex flex-col items-center">
              <RuneSpinner size={48} />
              <div className="text-sm font-semibold mt-4" style={{ color: TEXT_PRIMARY }}>{t('Deck Submitted')}</div>
              <div className="text-xs mt-1" style={{ color: TEXT_MUTED }}>{t('Waiting for other players...')}</div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
